import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useId, type SelectHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

const selectStyles = cva(
  'block w-full appearance-none rounded-md border bg-bg-canvas text-fg-primary transition-base ease-token focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50 disabled:bg-bg-subtle disabled:cursor-not-allowed bg-no-repeat pr-8',
  {
    variants: {
      size: {
        sm: 'h-8 pl-2 text-sm',
        md: 'h-10 pl-3 text-base',
        lg: 'h-12 pl-4 text-lg',
      },
      tone: {
        default: 'border-border-subtle hover:border-border-strong',
        error: 'border-danger focus-visible:ring-danger',
      },
    },
    defaultVariants: { size: 'md', tone: 'default' },
  },
);

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>, VariantProps<typeof selectStyles> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: readonly SelectOption[];
  placeholder?: string;
}

const chevron =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/></svg>\")";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, size, tone, label, hint, error, options, placeholder, id, ...props },
  ref,
) {
  const reactId = useId();
  const selectId = id ?? reactId;
  const describedById = hint || error ? `${selectId}-desc` : undefined;
  const resolvedTone = error ? 'error' : tone;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={selectId} className="text-fg-primary text-sm font-medium">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedById}
        className={cn(selectStyles({ size, tone: resolvedTone }), className)}
        style={{
          backgroundImage: chevron,
          backgroundPosition: 'right 0.625rem center',
          backgroundSize: '1rem 1rem',
        }}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      {(hint || error) && (
        <p id={describedById} className={cn('text-xs', error ? 'text-danger' : 'text-fg-muted')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
